import { ArrowRight02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export type Conflict = {
	field: string;
	label: string;
	oldValue: string;
	newValue: string;
};

const VALUE_FORMATTERS: Record<string, Record<string, string>> = {
	document_type: {
		id_card: "Občanský průkaz",
		passport: "Pas",
		drivers_license: "Řidičský průkaz",
	},
	citizenship: {
		czech: "České",
		other: "Jiné",
	},
	gender: {
		male: "Muž",
		female: "Žena",
	},
};

const valueToString = (value: unknown, field?: string): string | null => {
	if (value === null || value === undefined) return null;

	// Handle booleans - check type directly
	if (typeof value === "boolean") {
		return value ? "Ano" : "Ne";
	}

	// Handle enum fields with formatters
	if (field && VALUE_FORMATTERS[field] && typeof value === "string") {
		return (VALUE_FORMATTERS[field][value] ?? value) || null;
	}

	if (typeof value === "string") return value || null;
	return String(value) || null;
};

export const mergeValues = <T extends object>(
	currentValues: T,
	pendingValues: T,
): T => {
	const merged = { ...currentValues };
	for (const key of Object.keys(pendingValues) as (keyof T)[]) {
		if (valueToString(pendingValues[key]) !== null) {
			merged[key] = pendingValues[key];
		}
	}
	return merged;
};

export const findConflicts = <T extends object>(
	currentValues: T,
	pendingValues: T,
	fieldLabels: Record<string, string>,
	defaults?: Partial<T>,
): Conflict[] => {
	const result: Conflict[] = [];

	for (const [field, label] of Object.entries(fieldLabels)) {
		const newValue = valueToString(pendingValues[field as keyof T], field);
		if (newValue === null) continue;
		const currentValue = valueToString(currentValues[field as keyof T], field);
		if (currentValue === null) continue;
		const defaultValue = defaults
			? valueToString(defaults[field as keyof T], field)
			: null;
		if (currentValue !== defaultValue && currentValue !== newValue) {
			result.push({
				field,
				label,
				oldValue: currentValue,
				newValue: newValue,
			});
		}
	}
	return result;
};

interface ConflictDialogProps {
	conflicts: Conflict[];
	description: string;
	onApply: () => void;
	onCancel: () => void;
}

export function ConflictDialog({
	conflicts,
	description,
	onApply,
	onCancel,
}: ConflictDialogProps) {
	return (
		<AlertDialog
			open={true}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<AlertDialogContent className="shadow-xl sm:max-w-lg!">
				<AlertDialogHeader>
					<AlertDialogTitle>Přepsat vyplněné údaje?</AlertDialogTitle>
					<AlertDialogDescription className="text-wrap">
						{description} Některé pole, které jste vyplnili, se liší od
						načtených údajů:
					</AlertDialogDescription>
				</AlertDialogHeader>
				<table className="w-full text-sm">
					<tbody>
						{conflicts.map((conflict) => (
							<tr
								key={conflict.field}
								className="border-border border-b border-b-muted-foreground/10 last:border-b-0"
							>
								<td className="w-0 whitespace-nowrap py-2 pr-10 font-medium text-muted-foreground">
									{conflict.label}
								</td>
								<td className="max-w-32 truncate py-2 pr-4 font-medium text-destructive line-through decoration-[1.5px]">
									{conflict.oldValue}
								</td>
								<td className="w-0 py-2 pr-4 text-muted-foreground">
									<HugeiconsIcon
										icon={ArrowRight02Icon}
										className="size-4"
										strokeWidth={2}
									/>
								</td>
								<td className="max-w-32 truncate py-1 font-medium text-emerald-600">
									{conflict.newValue || "—"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				<AlertDialogFooter>
					<AlertDialogCancel size="lg" onClick={() => onCancel()}>
						Ponechat stávající hodnoty
					</AlertDialogCancel>
					<AlertDialogAction size="lg" onClick={() => onApply()}>
						Použít načtené údaje
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
